import React, { useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function VehicleSearch({ token, value, onChange, onVehicleSelect, placeholder = "Type vehicle number...", required = false, dataTestId = "vehicle-search-input" }) {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const handleSearch = async (searchValue) => {
    setInputValue(searchValue);
    onChange(searchValue);
    setSelectedVehicle(null);
    
    if (searchValue.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles/search?q=${searchValue}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSuggestions(data);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error searching vehicles:', error);
    }
  };

  const selectVehicle = async (vehicle) => {
    setInputValue(vehicle.vehicle_no);
    onChange(vehicle.vehicle_no);
    setShowSuggestions(false);
    
    // Fetch full vehicle details
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles/${vehicle.vehicle_no}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const fullVehicle = await response.json();
        setSelectedVehicle(fullVehicle);
        if (onVehicleSelect) {
          onVehicleSelect(fullVehicle);
        }
      }
    } catch (error) {
      console.error('Error fetching vehicle details:', error);
    }
  };

  // Sync with external value changes
  React.useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value]);

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        className="form-input"
        data-testid={dataTestId}
        value={inputValue}
        onChange={(e) => handleSearch(e.target.value)}
        onFocus={() => inputValue.length >= 2 && setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        placeholder={placeholder}
        required={required}
      />
      {showSuggestions && suggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: 'white',
          border: '1px solid #E2E8F0',
          borderRadius: '4px',
          marginTop: '4px',
          maxHeight: '200px',
          overflowY: 'auto',
          zIndex: 1000,
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }} data-testid="vehicle-suggestions">
          {suggestions.map((v, idx) => (
            <div
              key={idx}
              onClick={() => selectVehicle(v)}
              data-testid={`suggestion-${idx}`}
              style={{
                padding: '0.75rem',
                cursor: 'pointer',
                borderBottom: idx < suggestions.length - 1 ? '1px solid #F1F5F9' : 'none',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#F8FAFC'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <strong>{v.vehicle_no}</strong>
              {v.vehicle_make && ` - ${v.vehicle_make}`}
              {v.vehicle_model && ` ${v.vehicle_model}`}
            </div>
          ))}
        </div>
      )}
      {selectedVehicle && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.75rem',
          background: '#F0F9FF',
          border: '1px solid #BAE6FD',
          borderRadius: '6px',
          fontSize: '0.85rem'
        }} data-testid="selected-vehicle-info">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
            <div><strong>Make:</strong> {selectedVehicle.vehicle_make || '-'}</div>
            <div><strong>Model:</strong> {selectedVehicle.vehicle_model || selectedVehicle.vehicle_type || '-'}</div>
            <div><strong>Dept:</strong> {selectedVehicle.department || '-'}</div>
            {selectedVehicle.current_mileage && (
              <div><strong>Last Mileage:</strong> {selectedVehicle.current_mileage?.toLocaleString()} km</div>
            )}
            {selectedVehicle.owner && (
              <div><strong>Owner:</strong> {selectedVehicle.owner}</div>
            )}
          </div>
        </div>
      )}
      {inputValue.length >= 2 && suggestions.length === 0 && !showSuggestions && !selectedVehicle && (
        <p style={{ fontSize: '0.875rem', color: '#64748B', marginTop: '0.5rem' }}>
          No vehicles found. Try a different search.
        </p>
      )}
    </div>
  );
}

export default VehicleSearch;
